import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { waitForTranslationLoaded } from 'shared/util/wait-translations-loaded';
import { TranslateService } from '@ngx-translate/core';
import { debounce } from 'lodash-es';

@Component({
    selector: 'app-tab-header',
    templateUrl: './tab-header.component.html',
    styleUrls: ['./tab-header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
})
export class TabHeaderComponent implements AfterViewInit, OnDestroy {
    @ViewChild('scrollContainer', { read: ElementRef }) scrollContainer!: ElementRef;
    @ViewChild('tabHeader', { read: ElementRef }) tabHeader!: ElementRef;

    @HostListener('wheel', ['$event']) onWheelEvent(event: WheelEvent) {
        event.preventDefault();
        this.scrollContainer.nativeElement.scrollBy({ left: event.deltaY, behavior: 'auto' });
    }

    @HostListener('window:resize', ['$event']) onResize(event: Event) {
        this.computeButtonVisibilityDebounced();
    }

    // Debounce added to lower the number of calls to computeButtonVisibility
    // when the window is resized or the container is being scrolled through.
    // This lowers the number of calls by a factor of 100.
    computeButtonVisibilityDebounced = debounce(() => {
        this.computeButtonVisibility();
    }, 100);

    public showLeftScrollButton = signal<boolean>(false);
    public showRightScrollButton = signal<boolean>(false);

    private resizeObserver!: ResizeObserver;

    constructor(
        private scrollerElement: ElementRef,
        translateService: TranslateService,
    ) {
        waitForTranslationLoaded(translateService).subscribe(() => this.computeButtonVisibility());
    }

    public ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(() => {
            this.computeButtonVisibilityDebounced();
        });

        this.resizeObserver.observe(this.scrollContainer.nativeElement);

        this.computeButtonVisibility();
    }

    public ngOnDestroy(): void {
        this.resizeObserver.disconnect();
    }

    public computeButtonVisibility() {
        if (!this.scrollContainer || !this.tabHeader) {
            return;
        }
        const container = this.tabHeader.nativeElement;
        const scroller = this.scrollContainer.nativeElement;

        if (container.clientWidth > container.scrollWidth) {
            this.showLeftScrollButton.set(false);
            this.showRightScrollButton.set(false);

            return;
        }

        // Check if content overflows on the left side
        this.showLeftScrollButton.set(scroller.scrollLeft > 0);

        // Check if content overflows on the right side
        this.showRightScrollButton.set(scroller.scrollLeft + container.clientWidth < container.scrollWidth);
    }

    public scrollLeft() {
        this.scroll(-(this.scrollerElement.nativeElement.offsetWidth * 0.6 - 100));
    }

    public scrollRight() {
        this.scroll(this.scrollerElement.nativeElement.offsetWidth * 0.6 - 100);
    }

    private scroll(amount: number) {
        this.scrollContainer.nativeElement.scrollBy({ left: amount, behavior: 'smooth' });
    }
}
